/**
 * Tenant Overview → Domain tab (TMMPDM-0018): the tenant's whitelabel domain through tmm-api
 * (`/v1/admin/tenants/{slug}/whitelabel-domain…`, saas-auth's DTOs passed through).
 * `configured: false` is the normal "no custom domain" state. A configured domain shows the
 * server's `state` (DRAFT "Waiting for DNS" / ACTIVE "Live") — never derived from the hostname
 * flags — its hostnames, and, when the server composed them, the DNS records to create.
 *
 * Verify DNS runs saas-auth's per-hostname check (up to a minute; the call carries a 65 s timeout)
 * and refreshes the panel from the response. A 502 `SAAS_AUTH_OUTCOME_UNKNOWN` on any write
 * re-reads the domain and says so; nothing is re-sent.
 */
import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import { useTranslation } from 'react-i18next';
import { useCopy } from '@/hooks/useCopy';
import { ConfirmDialog, EmptyState, ErrorState, SubmitButton, useNotify } from '@/components/page';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { adminApi } from '@/api/tmm/admin';
import { errorCopy, isOutcomeUnknown } from '@/api/errors';
import type {
  WhitelabelDomainDto,
  WhitelabelDomainState,
  WhitelabelVerifyResponse,
} from '@/api/tmm/types';
import { SetWhitelabelDomainDialog } from './SetWhitelabelDomainDialog';
import { useHostnameTypeLabel } from './useHostnameTypeLabel';
import { formatInstant } from '@/format';
import { StatusChip } from '@/status';

interface WhitelabelDomainPanelProps {
  slug: string;
}

type Notice = { severity: 'success' | 'warning' | 'error'; message: string };

/*
 * The chips below are keyed per value so a change (DRAFT → ACTIVE, not verified → verified)
 * mounts a new chip instead of animating the old one's background through a mid-transition
 * colour that fails the 4.5:1 contrast floor while it fades.
 */
function StateChip({ state }: { state: WhitelabelDomainState | null }) {
  const { t } = useCopy();
  if (state === 'ACTIVE') {
    return <StatusChip key="ACTIVE" status="ACTIVE" label={t('whitelabel.state.ACTIVE', 'Live')} />;
  }
  if (state === 'DRAFT') {
    return (
      // The registry's DRAFT is outlined ("not yet in effect"); a domain waiting for DNS needs
      // someone to act, so it stays a warning — a §5 change request, see the TMMPDM-0065 delivery.
      <StatusChip
        key="DRAFT"
        tone="warning"
        label={t('whitelabel.state.DRAFT', 'Waiting for DNS')}
      />
    );
  }
  return <StatusChip key="other" label={state ?? '—'} />;
}

function VerifiedChip({ verified }: { verified: boolean }) {
  const { t } = useCopy();
  return verified ? (
    <StatusChip key="verified" tone="success" label={t('whitelabel.verified', 'Verified')} />
  ) : (
    // Outlined means draft: a hostname that is not yet verified needs someone's action, so warning.
    <StatusChip
      key="not-verified"
      tone="warning"
      label={t('whitelabel.notVerified', 'Not verified')}
    />
  );
}

/** A monospace value with a copy button (DNS records are typed into a DNS provider by hand). */
function CopyableValue({ value }: { value: string }) {
  const { t } = useCopy();
  const notify = useNotify();
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      notify.success(t('components.copied', 'Copied'));
    } catch {
      setCopied(false);
    }
  };
  return (
    <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
      <Box component="code" sx={(theme) => ({ ...theme.typography.mono, wordBreak: 'break-all' })}>
        {value}
      </Box>
      <IconButton
        size="small"
        aria-label={t('whitelabel.copy', 'Copy {{value}}', { value })}
        onClick={() => void copy()}
      >
        {copied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
      </IconButton>
    </Stack>
  );
}

/**
 * The verify response carries no `hostnames`; each hostname's persisted `dnsVerified` comes
 * from its `results[]` row (the server's value, matched by hostname), everything else from the
 * response's own fields.
 */
function applyVerify(
  dto: WhitelabelDomainDto,
  result: WhitelabelVerifyResponse,
): WhitelabelDomainDto {
  const byHostname = new Map(result.results.map((row) => [row.hostname, row]));
  return {
    ...dto,
    domain: result.domain,
    state: result.state,
    activatedAt: result.activatedAt,
    dnsConfigured: result.dnsConfigured,
    dnsRecords: result.dnsRecords ?? null,
    hostnames: dto.hostnames.map((host) => {
      const row = byHostname.get(host.hostname);
      return row ? { ...host, dnsVerified: row.dnsVerified } : host;
    }),
  };
}

export function WhitelabelDomainPanel({ slug }: WhitelabelDomainPanelProps) {
  const { t } = useCopy();
  const { i18n } = useTranslation();
  const typeLabel = useHostnameTypeLabel();
  const [reloadToken, setReloadToken] = useState(0);
  const requestKey = `${slug}:${reloadToken}`;
  const [response, setResponse] = useState<{
    requestKey: string | null;
    dto: WhitelabelDomainDto | null;
    failed: boolean;
    error: unknown;
  }>({ requestKey: null, dto: null, failed: false, error: null });
  const [verifyResult, setVerifyResult] = useState<{
    requestKey: string;
    result: WhitelabelVerifyResponse;
  } | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [removeOpen, setRemoveOpen] = useState(false);
  const [removing, setRemoving] = useState(false);
  // A refused removal is said inside its confirm, which stays open (TMM-B4).
  const [removeError, setRemoveError] = useState<string | null>(null);
  const notify = useNotify();
  const [notice, setNotice] = useState<Notice | null>(null);

  useEffect(() => {
    let cancelled = false;
    adminApi
      .getWhitelabel(slug)
      .then((dto) => {
        if (!cancelled) setResponse({ requestKey, dto, failed: false, error: null });
      })
      .catch((error: unknown) => {
        if (!cancelled) setResponse({ requestKey, dto: null, failed: true, error });
      });
    return () => {
      cancelled = true;
    };
  }, [slug, requestKey]);

  const current = response.requestKey === requestKey;
  const dto = current ? response.dto : null;
  const lastCheck = verifyResult?.requestKey === requestKey ? verifyResult.result : null;

  const reload = () => setReloadToken((n) => n + 1);

  const reloadAfterUnknownOutcome = () => {
    setNotice({
      severity: 'warning',
      message: t(
        'common.outcomeUnknown',
        "We couldn't confirm the change. The screen has been reloaded — check before trying again.",
      ),
    });
    reload();
  };

  const handleVerify = async () => {
    if (!dto) return;
    setNotice(null);
    setVerifying(true);
    try {
      const result = await adminApi.verifyWhitelabel(slug);
      setResponse({ requestKey, dto: applyVerify(dto, result), failed: false, error: null });
      setVerifyResult({ requestKey, result });
    } catch (err) {
      if (isOutcomeUnknown(err)) {
        reloadAfterUnknownOutcome();
      } else {
        setNotice({
          severity: 'error',
          message: errorCopy(err, t, t('whitelabel.verifyError', 'Could not verify DNS.')),
        });
      }
    } finally {
      setVerifying(false);
    }
  };

  const handleRemove = async () => {
    setNotice(null);
    setRemoveError(null);
    setRemoving(true);
    try {
      await adminApi.deleteWhitelabel(slug);
      setRemoveOpen(false);
      notify.success(t('whitelabel.removed', 'Custom domain removed.'));
      reload();
    } catch (err) {
      if (isOutcomeUnknown(err)) {
        setRemoveOpen(false);
        reloadAfterUnknownOutcome();
        return;
      }
      setRemoveError(
        errorCopy(err, t, t('whitelabel.removeError', 'Could not remove the custom domain.')),
      );
    } finally {
      setRemoving(false);
    }
  };

  return (
    <Box>
      <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 600, mb: 1 }}>
        {t('whitelabel.title', 'Custom domain')}
      </Typography>

      {notice ? (
        <Alert severity={notice.severity} sx={{ mb: 2 }} onClose={() => setNotice(null)}>
          {notice.message}
        </Alert>
      ) : null}

      {!current ? <LoadingSpinner /> : null}
      {current && response.failed ? (
        <ErrorState
          error={response.error}
          message={t('whitelabel.loadError', 'Could not load the custom domain.')}
          onRetry={reload}
        />
      ) : null}

      {dto && !dto.configured ? (
        <EmptyState
          surface
          title={t('whitelabel.none', 'No custom domain. Tenants use the platform hostnames.')}
          action={
            <Button variant="contained" onClick={() => setDialogOpen(true)}>
              {t('whitelabel.setUp', 'Set up a domain…')}
            </Button>
          }
        />
      ) : null}

      {dto?.configured ? (
        <>
          <TableContainer component={Paper} variant="outlined" sx={{ maxWidth: 560, mb: 2 }}>
            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell component="th" scope="row">
                    {t('whitelabel.domain', 'Domain')}
                  </TableCell>
                  <TableCell>{dto.domain ?? '—'}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell component="th" scope="row">
                    {t('whitelabel.stateLabel', 'State')}
                  </TableCell>
                  <TableCell>
                    <StateChip state={dto.state} />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell component="th" scope="row">
                    {t('whitelabel.activatedAt', 'Activated')}
                  </TableCell>
                  <TableCell>{formatInstant(dto.activatedAt, i18n.language)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', rowGap: 1 }}>
            <SubmitButton onClick={() => void handleVerify()} busy={verifying}>
              {t('whitelabel.verify', 'Verify DNS')}
            </SubmitButton>
            <Button variant="outlined" onClick={() => setDialogOpen(true)} disabled={verifying}>
              {t('whitelabel.change', 'Change domain…')}
            </Button>
            <Button
              variant="outlined"
              color="error"
              onClick={() => setRemoveOpen(true)}
              disabled={verifying}
            >
              {t('whitelabel.remove', 'Remove…')}
            </Button>
          </Stack>

          {verifying ? (
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 2 }} role="status">
              <CircularProgress size={20} aria-label={t('whitelabel.verifying', 'Verifying DNS')} />
              <Typography variant="body2">
                {t('whitelabel.verifyWait', 'This can take up to a minute.')}
              </Typography>
            </Stack>
          ) : null}

          {lastCheck ? (
            <Box sx={{ mb: 3 }}>
              <Alert severity={lastCheck.allVerified ? 'success' : 'warning'} sx={{ mb: 1 }}>
                {lastCheck.allVerified
                  ? t('whitelabel.results.allVerified', 'Every hostname is verified.')
                  : t(
                      'whitelabel.results.someFailed',
                      'Some hostnames did not verify. Check the DNS records and try again.',
                    )}
              </Alert>
              <Typography variant="subtitle2" component="h3" sx={{ mb: 1 }}>
                {t('whitelabel.results.title', 'Latest check')}
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small" aria-label={t('whitelabel.results.title', 'Latest check')}>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('whitelabel.col.hostname', 'Hostname')}</TableCell>
                      <TableCell>{t('whitelabel.col.type', 'Type')}</TableCell>
                      <TableCell>{t('whitelabel.results.thisCheck', 'This check')}</TableCell>
                      <TableCell>{t('whitelabel.col.dns', 'DNS')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {lastCheck.results.map((row) => (
                      <TableRow key={row.hostname}>
                        <TableCell>{row.hostname}</TableCell>
                        <TableCell>{typeLabel(row.hostnameType)}</TableCell>
                        <TableCell>
                          {row.matched
                            ? t('whitelabel.results.matched', 'Matched')
                            : t('whitelabel.results.notMatched', 'No match')}
                        </TableCell>
                        <TableCell>
                          <VerifiedChip verified={row.dnsVerified} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          ) : null}

          <Typography variant="subtitle2" component="h3" sx={{ mb: 1 }}>
            {t('whitelabel.hostnames', 'Hostnames')}
          </Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
            <Table size="small" aria-label={t('whitelabel.hostnames', 'Hostnames')}>
              <TableHead>
                <TableRow>
                  <TableCell>{t('whitelabel.col.hostname', 'Hostname')}</TableCell>
                  <TableCell>{t('whitelabel.col.type', 'Type')}</TableCell>
                  <TableCell>{t('whitelabel.col.app', 'App')}</TableCell>
                  <TableCell>{t('whitelabel.col.dns', 'DNS')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {dto.hostnames.map((host) => (
                  <TableRow key={host.id}>
                    <TableCell>{host.hostname}</TableCell>
                    <TableCell>{typeLabel(host.hostnameType)}</TableCell>
                    <TableCell>{host.appSlug ?? '—'}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                        <VerifiedChip verified={host.dnsVerified} />
                        {host.dnsVerifiedAt ? (
                          <Typography variant="caption" color="text.secondary">
                            {formatInstant(host.dnsVerifiedAt, i18n.language)}
                          </Typography>
                        ) : null}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {dto.dnsRecords != null ? (
            <>
              <Typography variant="subtitle2" component="h3" sx={{ mb: 1 }}>
                {t('whitelabel.records.title', 'DNS records to create')}
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table
                  size="small"
                  aria-label={t('whitelabel.records.title', 'DNS records to create')}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('whitelabel.records.host', 'Host')}</TableCell>
                      <TableCell>{t('whitelabel.records.type', 'Type')}</TableCell>
                      <TableCell>{t('whitelabel.records.target', 'Target')}</TableCell>
                      <TableCell>{t('whitelabel.records.verified', 'Verified')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dto.dnsRecords.map((record) => (
                      <TableRow key={record.host}>
                        <TableCell>
                          <CopyableValue value={record.host} />
                        </TableCell>
                        <TableCell>
                          <CopyableValue value={record.type} />
                        </TableCell>
                        <TableCell>
                          <CopyableValue value={record.target} />
                        </TableCell>
                        <TableCell>
                          <VerifiedChip verified={record.dnsVerified} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          ) : null}
        </>
      ) : null}

      <SetWhitelabelDomainDialog
        open={dialogOpen}
        slug={slug}
        existing={dto?.configured ? dto : null}
        onClose={() => setDialogOpen(false)}
        onSaved={(saved) => {
          setDialogOpen(false);
          setResponse({ requestKey, dto: saved, failed: false, error: null });
          setVerifyResult(null);
          setNotice({
            severity: 'success',
            message: t(
              'whitelabel.set.saved',
              'Domain saved. Create the DNS records, then verify.',
            ),
          });
        }}
        onOutcomeUnknown={() => {
          setDialogOpen(false);
          reloadAfterUnknownOutcome();
        }}
      />

      <ConfirmDialog
        open={removeOpen}
        title={t('whitelabel.removeTitle', 'Remove the custom domain?')}
        consequence={t(
          'whitelabel.removeBody',
          'The tenant goes back to the platform hostnames. DNS records you created can be deleted afterwards.',
        )}
        confirmLabel={t('whitelabel.removeConfirm', 'Remove')}
        destructive
        busy={removing}
        error={removeError}
        onConfirm={() => void handleRemove()}
        onCancel={() => {
          setRemoveOpen(false);
          setRemoveError(null);
        }}
      />
    </Box>
  );
}
